import { useCallback, useEffect, useMemo, useState } from 'react';
import { realtimeSupports } from '../../config/products';
import { realtimeApi } from '../../services/realtimeApi';
import { userMapsApi } from '../../services/userMapsApi';
import {
  hiddenAreasNote,
  hideElsewhereAreas,
  partitionMonitored,
  withRealtimeCountry,
} from '../../utils/realtimeAreaOrder';
import { realtimeDataIsHere, realtimeEnvLabel } from '../../utils/realtimeEnv';
import { usePollingRefresh } from './usePollingRefresh';

export interface RealtimeArea {
  id: string;
  name: string;
  description: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code?: string | null;
  bqProjectId?: string;
}

/**
 * The saved areas as the Real Time and Emergency pages list them: which are being monitored, which
 * have data, and the two ordered lists (monitored first, then the rest) with the areas whose data
 * lives in another environment left out and counted in a note. Only the realtime and emergency
 * pages use it, because it is the one place that asks about the environment's realtime data.
 *
 * `refreshMs` re-reads the data counts quietly on that interval (tab visible only).
 */
export function useRealtimeAreas({ refreshMs }: { refreshMs?: number } = {}) {
  const [savedAreas, setSavedAreas] = useState<RealtimeArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monitoringStates, setMonitoringStates] = useState<Record<string, boolean>>({});
  // Row counts in the realtime dataset, keyed by both target_id and target_name so a saved area can
  // be matched on either. Loaded separately so a realtime failure never blocks the area list.
  const [realtimeCounts, setRealtimeCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load saved areas and monitored maps in parallel
        const [geometriesResponse, monitoredResponse] = await Promise.all([
          userMapsApi.loadGeometries(),
          realtimeApi.getMonitoredMaps(),
        ]);

        const sortedAreas = [...geometriesResponse.maps].sort((a, b) => a.name.localeCompare(b.name));
        setSavedAreas(sortedAreas);

        const initialStates: Record<string, boolean> = {};
        sortedAreas.forEach((area: RealtimeArea) => {
          initialStates[area.id] = monitoredResponse.monitored_map_ids.includes(area.id);
        });
        setMonitoringStates(initialStates);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Best-effort: figure out which polygons actually have realtime data.
  const loadCounts = useCallback(async (isCancelled: () => boolean = () => false) => {
    try {
      const { rows } = await realtimeApi.getRealtimeData();
      if (isCancelled()) return;
      const counts: Record<string, number> = {};
      rows.forEach((r) => {
        // Unbounded range, so the backend may return hour/day aggregates: count the samples behind
        // each row so this stays a sample count (it only drives a has-data sort).
        const n = r.sample_count ?? 1;
        counts[r.target_id] = (counts[r.target_id] || 0) + n;
        counts[r.target_name] = (counts[r.target_name] || 0) + n;
      });
      setRealtimeCounts(counts);
    } catch (err) {
      console.warn('Could not load realtime availability:', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadCounts(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loadCounts]);

  usePollingRefresh(() => void loadCounts(), refreshMs ?? 0, refreshMs !== undefined);

  const dataCountFor = useCallback(
    (area: { id: string; name: string }): number => realtimeCounts[area.id] ?? realtimeCounts[area.name] ?? 0,
    [realtimeCounts],
  );

  // Monitored (turned on) polygons first, then the rest. Inside each group the polygons that have
  // realtime data come first, then by name. A polygon with no country is shown under the Real Time
  // country (display only). Polygons whose Real Time data lives in another environment can't be shown
  // here, so they are left out of both lists; a muted line under the lists says how many.
  const { monitoredAreas, otherAreas, hiddenNote } = useMemo(() => {
    const shown = savedAreas.map(withRealtimeCountry);
    const isMonitored = (id: string) => !!monitoringStates[id];
    const elsewhere = (a: RealtimeArea) => realtimeSupports(a) && !realtimeDataIsHere(a.bqProjectId);
    const { visible, hidden, hiddenMonitored } = hideElsewhereAreas(shown, elsewhere, isMonitored);
    const { monitored, other } = partitionMonitored(visible, isMonitored, dataCountFor);
    const labels = shown.filter(elsewhere).map((a) => realtimeEnvLabel(a.bqProjectId));
    return {
      monitoredAreas: monitored,
      otherAreas: other,
      hiddenNote: hiddenAreasNote(hidden, hiddenMonitored, labels),
    };
  }, [savedAreas, monitoringStates, dataCountFor]);

  return {
    savedAreas,
    loading,
    error,
    monitoringStates,
    setMonitoringStates,
    dataCountFor,
    monitoredAreas,
    otherAreas,
    hiddenNote,
  };
}
